import { useState } from 'react'
import PropTypes from 'prop-types'
import { format } from 'date-fns'
import {
  PencilSimpleLine,
  CaretUp,
  CaretDown,
  CurrencyDollar,
  Receipt,
  Info,
} from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { formatDollars } from '@/utils'
import { Badge } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'

import CheckboxField from '../../FormLegacy/FormFieldsV2/CheckboxField'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'

import { getChargeBalance, getChargePayable, getChargeProcessing, isChargeLocked } from './helpers'

const YardiBillList = ({
  transactions,
  selectedTransactions,
  editTransaction,
  totalPay,
  errorMessage,
  onEditPrice,
  handleChangeValue,
  saveNewValue,
  getOriginalValue,
  setTransactionDetails,
  manageSelectedTransaction,
  paymentSettings,
}) => {
  const { t } = useTranslation(['organisation', 'common'])
  const [isOpen, setOpen] = useState(null)

  const handleOpenClose = (transaction) => {
    if (isOpen === transaction.chargeCode) {
      setOpen(null)
    } else {
      setOpen(transaction.chargeCode)
    }
  }

  return (
    <div className="transaction-details-list">
      {transactions.map((transaction) => (
        <div key={transaction.chargeCode} className="transaction-item">
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              paddingBottom: 10,
            }}
          >
            <div className="left-side">
              {(() => {
                const details = (
                  <div className="column-flex">
                    <div className="transaction-name">{transaction.description}</div>
                    <div className="grey-header">
                      {t('organisation:payments.billList.due', {
                        date: format(
                          new Date(transaction.transactions[0].transactionDate),
                          'MMMM, do',
                        ),
                      })}
                    </div>
                  </div>
                )
                return paymentSettings.allowChargesSelection ? (
                  <CheckboxField
                    name={`toggle-${transaction.chargeCode}`}
                    value={selectedTransactions.includes(transaction.chargeCode)}
                    disabled={isChargeLocked(transaction)}
                    onChange={() =>
                      !isChargeLocked(transaction) &&
                      manageSelectedTransaction(transaction.chargeCode)
                    }
                    label={details}
                  />
                ) : (
                  details
                )
              })()}
            </div>
            <div className="right-side">
              <div className="column-flex">
                <div className="grey-header">{t('organisation:payments.billList.totalPay')}</div>
                {editTransaction && editTransaction.chargeCode === transaction.chargeCode ? (
                  <div className="row-flex">
                    <CurrencyDollar
                      size={16}
                      style={{ position: 'absolute', zIndex: 1, top: 7, left: 6 }}
                    />
                    <TextField
                      name="pariceValue"
                      type="number"
                      onChange={handleChangeValue}
                      value={totalPay}
                      style={{ width: '100%' }}
                      isError={!!errorMessage}
                      errorLabel={errorMessage}
                    />
                    <button className="button button-bordered" onClick={getOriginalValue}>
                      {t('organisation:payments.billList.payInFull')}
                    </button>
                    <button
                      className="button button-black"
                      onClick={saveNewValue}
                      disabled={!!errorMessage}
                    >
                      {t('common:actions.save')}
                    </button>
                  </div>
                ) : isChargeLocked(transaction) ? (
                  <Badge color="orange" size="small">
                    {t('organisation:payments.processing.badge')}
                  </Badge>
                ) : paymentSettings.allowChargesSelection && paymentSettings.allowPartialPayment ? (
                  <div className="price-row-flex" onClick={() => onEditPrice(transaction)}>
                    <div className="transaction-value">
                      {formatDollars(transaction.fixedPrice || getChargePayable(transaction), {
                        zeroPrice: true,
                      })}
                    </div>
                    <PencilSimpleLine size={18} />
                  </div>
                ) : (
                  <div className="price-row-flex-nopointer">
                    <div className="transaction-value">
                      {formatDollars(transaction.fixedPrice || getChargePayable(transaction), {
                        zeroPrice: true,
                      })}
                    </div>
                  </div>
                )}
                {getChargeProcessing(transaction) > 0 && (
                  <Typography size="text-small" color="orange700">
                    {t('organisation:payments.processing.inProgress', {
                      amount: getChargeProcessing(transaction),
                    })}
                  </Typography>
                )}
              </div>
              <div className="accordin-drop" onClick={() => handleOpenClose(transaction)}>
                {isOpen === transaction.chargeCode ? (
                  <CaretUp size={24} />
                ) : (
                  <CaretDown size={24} />
                )}
              </div>
            </div>
          </div>
          {isOpen === transaction.chargeCode && (
            <div className="border-wrap">
              {transaction.transactions.map((transaction, idx) => (
                <div key={idx} className="transaction-container">
                  <div className="column-flex">
                    <div className="simple-row-flex">
                      <Receipt size={20} />
                      <div className="item-title">{transaction.comment}</div>
                    </div>
                    <div className="item-due-date">
                      {format(new Date(transaction.transactionDate), 'MMMM, do')}
                    </div>
                  </div>
                  <div className="simple-row-flex">
                    <div>
                      <div className="default-header">
                        {t('organisation:payments.billDetails.balanceDue')}
                      </div>
                      <div className="simple-row-flex">
                        <div className="transaction-price">
                          {formatDollars(transaction.balanceDue, { zeroPrice: true })}
                        </div>
                      </div>
                    </div>
                    <div
                      className="transaction-info"
                      onClick={() => setTransactionDetails(transaction)}
                    >
                      <Info size={20} />
                    </div>
                  </div>
                </div>
              ))}
              <div className="transaction-container">
                <div className="summary-header">
                  {t('organisation:payments.payRentYardi.totalDue')}
                </div>
                <div className="summary-header">
                  {formatDollars(getChargeBalance(transaction), { zeroPrice: true })}
                </div>
              </div>
              {getChargeProcessing(transaction) > 0 && (
                <>
                  <div className="transaction-container">
                    <div className="default-header">
                      {t('organisation:payments.processing.label')}
                    </div>
                    <div className="default-header">
                      {formatDollars(getChargeProcessing(transaction), { zeroPrice: true })}
                    </div>
                  </div>
                  <div className="transaction-container">
                    <div className="summary-header">
                      {t('organisation:payments.processing.availableToPay')}
                    </div>
                    <div className="summary-header">
                      {formatDollars(getChargePayable(transaction), { zeroPrice: true })}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

YardiBillList.propTypes = {
  transactions: PropTypes.array,
  selectedTransactions: PropTypes.array,
  editTransaction: PropTypes.object,
  totalPay: PropTypes.string,
  errorMessage: PropTypes.string,
  onEditPrice: PropTypes.func,
  handleChangeValue: PropTypes.func,
  saveNewValue: PropTypes.func,
  getOriginalValue: PropTypes.func,
  setTransactionDetails: PropTypes.func,
  manageSelectedTransaction: PropTypes.func,
  paymentSettings: PropTypes.object,
}

export default YardiBillList
